import type { PlatformSessionRow } from "./platform-sessions";

/** Client-side redaction until the operator explicitly reveals contact. */
export function redactSignInEmail(email: string | null, revealed: boolean): string {
  if (email === null) return "Not provided";
  if (revealed) return email;
  const separator = email.indexOf("@");
  if (separator <= 0) return "•••";
  return `${email.slice(0, 1)}•••${email.slice(separator)}`;
}

export function sessionPersonTitle(row: PlatformSessionRow): string {
  const name = row.personDisplayName?.trim();
  if (name !== undefined && name.length > 0) {
    return name;
  }
  return row.userLabel;
}

export function sessionAccountSubtitle(row: PlatformSessionRow): string | null {
  if (row.accountNames !== null && row.accountNames.length > 0) {
    if (row.workspaceCount > 1) {
      return `${row.accountNames} · ${row.workspaceCount} workspaces`;
    }
    return row.accountNames;
  }
  if (row.accountTail !== "—" && row.accountTail !== "none" && row.accountTail !== "platform") {
    return `Tail ${row.accountTail}`;
  }
  return null;
}

const STALE_MS = 24 * 60 * 60 * 1000;

export function sessionActivityTone(lastRefreshedAt: string | null, nowMs: number): "fresh" | "stale" | "unknown" {
  if (lastRefreshedAt === null) {
    return "unknown";
  }
  const parsed = Date.parse(lastRefreshedAt);
  if (!Number.isFinite(parsed)) {
    return "unknown";
  }
  return nowMs - parsed <= STALE_MS ? "fresh" : "stale";
}

export function matchesSessionSearch(row: PlatformSessionRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }
  const haystack = [
    row.personDisplayName,
    row.accountNames,
    row.signInEmail,
    row.userLabel,
    row.role,
    row.accountTail,
    row.surfaceHint,
    row.ip,
  ]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

export function uniqueAccountNames(rows: readonly PlatformSessionRow[]): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.accountNames === null) {
      continue;
    }
    for (const part of row.accountNames.split(",")) {
      const trimmed = part.trim();
      if (trimmed.length > 0) {
        names.add(trimmed);
      }
    }
  }
  return [...names].sort((left, right) => left.localeCompare(right));
}
